/**
 * Vector class
 *      - a helper class that computes vector math
 */
class Vector {
    constructor (x, y) {
        this.properties = { x, y }
    }

    setX (value) {
        this.properties.x = value
        return this
    }

    getX () {
        return this.properties.x
    }

    setY (value) {
        this.properties.y = value
        return this
    }

    getY () {
        return this.properties.y
    }

    setDirection (angle, isRadians) {
        let hypotenuse = this.getMagnitude()
        this.setX(hypotenuse *
            Math.cos(isRadians ? angle : this.angleToRadians(angle)))
        this.setY(hypotenuse *
            Math.sin(isRadians ? angle : this.angleToRadians(angle)))

        return this
    }

    getDirection (inRadians) {
        let radians = this.getAngle(true)

        if (this.getY() < 0) {
            if (this.getX() < 0) {
                return inRadians ? Math.PI - radians : this.radiansToAngle(Math.PI - radians)
            }
            return inRadians ? radians : this.radiansToAngle(radians)
        }
        if (this.getX() < 0) {
            return inRadians ? Math.PI + radians : this.radiansToAngle(Math.PI + radians)
        }
        return inRadians ? 2 * Math.PI - radians : this.radiansToAngle(2 * Math.PI - radians)
    }

    getAngle (inRadians) {
        let radians = Math.atan(Math.abs(this.getY()) / (Math.abs(this.getX())))
        return inRadians ? radians : this.radiansToAngle(radians)
    }

    setMagnitude (hypotenuse) {
        let radians = this.getAngle(true)
        let direction = this.getDirection()
        let adjacent = hypotenuse * Math.cos(radians)
        let oposite = hypotenuse * Math.sin(radians)

        if (direction < 90) {
            return this.setX(adjacent)
                .setY(-oposite)
        }

        if (direction < 180) {
            return this.setX(-adjacent)
                .setY(-oposite)
        }
        if (direction < 270) {
            return this.setX(-adjacent)
                .setY(oposite)
        }

        return this.setX(adjacent)
            .setY(oposite)
    }

    getMagnitude () {
        return Math.sqrt(
            Math.pow(this.getX(), 2) + Math.pow(this.getY(), 2),
        )
    }

    add (vector) {
        return this.setX(this.getX() + vector.getX())
            .setY(this.getY() + vector.getY())
    }

    subtract (vector) {
        return this.add(vector.clone().invert())
    }

    multiply (multiplier) {
        return this.setX(this.getX() * multiplier)
            .setY(this.getY() * multiplier)
    }

    divide (divider) {
        return this.setX(this.getX() / divider)
            .setY(this.getY() / divider)
    }

    invert () {
        return this.setX(-this.getX())
            .setY(-this.getY())
    }

    limit (amount) {
        if (this.getMagnitude() > amount) {
            return this.setMagnitude(amount)
        }
        return this
    }

    clone () {
        return new Vector(this.getX(), this.getY())
    }

    angleToRadians (value) {
        return value * (Math.PI / 180)
    }

    radiansToAngle (value) {
        return value * (180 / Math.PI)
    }
}

export default Vector
